/**
 * ScanStatusModal - Advanced modal for viewing and managing jobs
 *
 * Features:
 * - Status filter dropdown with counts
 * - Search by job name or DNS
 * - Bulk selection with Rerun/Cancel actions
 * - Sorted by updated timestamp (newest first) by default
 *
 * Used in Asset, Vulnerability, and Capability drawers.
 */

import { useDeferredValue, useMemo, useState } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { Modal } from '@/components/Modal';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SearchBar } from '@/components/ui/SearchBar';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { JobsActions } from '@/sections/jobs/components/actions';
import { getStatusIcon } from '@/sections/jobs/components/cells';
import { JobsTable } from '@/sections/jobs/components/JobsTable';
import { getJobActionOptions } from '@/sections/jobs/config/actionOptions/getJobActionOptions';
import { Job, JobStatus, JobStatusLabel } from '@/types';
import { getJobStatus } from '@/utils/job';

import { ScanStatusModalProps } from './types';

/**
 * ScanStatusModal Component
 *
 * Renders a modal with a jobs table, filtering, search, and bulk actions.
 */
export function ScanStatusModal({
  open,
  onClose,
  title,
  jobs,
  isLoading,
  error,
  refetch,
  actionHandlers,
}: ScanStatusModalProps) {
  // Selection state for bulk actions
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Sort state - default to newest first
  const [sort, setSort] = useState<{ orderBy: keyof Job; descending: boolean }>(
    {
      orderBy: 'updated',
      descending: true,
    }
  );

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  // Status filter options with counts
  const statusFilters = useMemo(() => {
    const statusCounts = jobs.reduce(
      (acc, job) => {
        const jobStatus = getJobStatus(job);
        acc[jobStatus] = (acc[jobStatus] || 0) + 1;
        return acc;
      },
      {} as Partial<Record<JobStatus, number>>
    );

    return Object.entries(JobStatusLabel)
      .filter(
        ([value]) =>
          value !== JobStatus.AsyncRunning &&
          value !== JobStatus.AsyncReturning &&
          value !== JobStatus.Partial &&
          statusCounts[value as JobStatus]
      )
      .map(([value, label]) => ({
        id: `status-${value}`,
        label,
        value: `status:${value}`,
        count: statusCounts[value as JobStatus] || 0,
        icon: (
          <div className="pr-3">{getStatusIcon(value as JobStatus, false)}</div>
        ),
      }));
  }, [jobs]);

  // Filtered jobs based on status and search
  const filteredJobs = useMemo(() => {
    let result = jobs;

    if (statusFilter.length > 0) {
      const statusValue = statusFilter[0].replace('status:', '');
      result = result.filter(job => getJobStatus(job) === statusValue);
    }

    if (deferredSearchQuery) {
      const query = deferredSearchQuery.toLowerCase();
      result = result.filter(
        job =>
          job.name?.toLowerCase().includes(query) ||
          job.dns?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [jobs, statusFilter, deferredSearchQuery]);

  // Force table remount on empty -> data transition
  const isFilteredEmpty = !isLoading && filteredJobs.length === 0;
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    filteredJobs.length
  );

  // Selected jobs from keys
  const selectedJobs = useMemo(
    () =>
      selectedRows
        .map(key => filteredJobs.find(job => job.key === key))
        .filter((job): job is Job => job !== undefined),
    [selectedRows, filteredJobs]
  );

  // Handle modal close with state reset
  const handleClose = () => {
    setSelectedRows([]);
    setSort({ orderBy: 'updated', descending: true });
    setStatusFilter([]);
    setSearchQuery('');
    onClose();
  };

  const hasActiveFilters = statusFilter.length > 0 || searchQuery.length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="7xl"
      hideFooter
      className="mb-4"
    >
      <TableControlsContainer
        filterLabel={true}
        filters={
          <>
            <FilterDropdown
              id="status"
              label="Status"
              options={statusFilters}
              selectedValues={statusFilter}
              maxSelections={1}
              onApply={selected => {
                setStatusFilter(selected.map(opt => String(opt.value)));
              }}
              onClear={() => setStatusFilter([])}
              width={300}
            />
            {hasActiveFilters && (
              <ClearFiltersButton
                onClick={() => {
                  setStatusFilter([]);
                  setSearchQuery('');
                }}
                label="Clear"
              />
            )}
          </>
        }
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64 shrink-0"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search"
          />
        }
        selectionBar={
          <TableSelectionBar
            embedded={true}
            alwaysVisible={true}
            isVisible={selectedRows.length > 0}
            selectedCount={selectedJobs.length}
            totalCount={filteredJobs.length}
            hasNextPage={false}
            resourceLabel="jobs"
            emptySelectionMessage="Select jobs to perform bulk actions"
            actions={
              <JobsActions
                jobs={selectedJobs}
                selectedRowsCount={selectedRows.length}
                onClearSelection={() => setSelectedRows([])}
                refetch={refetch}
                hideScanStatus={true}
                disabled={selectedJobs.length === 0}
              />
            }
          />
        }
      />
      <JobsTable
        key={tableKey}
        name="scan-status-jobs"
        jobs={filteredJobs}
        status={isLoading ? 'pending' : 'success'}
        error={error}
        responsive={false}
        enableSelection={true}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        maxHeight={480}
        minHeight={480}
        sort={{
          orderBy: sort.orderBy,
          descending: sort.descending,
          onSort: setSort,
        }}
        rowActions={job => ({
          menu: {
            items: getJobActionOptions({
              job,
              handlers: {
                handleRerun: actionHandlers.handleRerun,
                handleCancel: actionHandlers.handleCancel,
                handleDownload:
                  actionHandlers.handleDownload ?? (() => undefined),
              },
            }),
          },
        })}
        emptyState={{
          title: 'No Jobs Found',
          description: 'No jobs match your current filters.',
        }}
      />
    </Modal>
  );
}
